import type { JobWithDetails } from "@/api/hooks/useJob";
import type { Job } from "@/api/hooks/useJobs";
import type { User } from "@/api/hooks/useMe";
import { useApi } from "@mb-pro-ui/utils";
import {
  AccessTime,
  CheckCircleOutline,
  Flag,
  LocationOn,
  Navigation,
  Person,
  PlayArrow,
} from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "wouter";
import { statusConfig } from "../constants/status";
import { useJobsContext } from "../contexts/JobsContexts";
import { AppHeader } from "./AppHeader";
import CallbackRequest from "./CallbackRequest";
import JobLogChat from "./JobLogChat";
import PhotoGalleryCapture from "./PhotoGalleryCapture";
import ResponseJobReportForm from "./ResponseJobReportForm";
import { SignatureField } from "./SignatureCanvas";
import { formatDateTime, useRelativeTime } from "./formatDate";

type JobDetailsProps = {
  job: JobWithDetails;
  onBack?: () => void;
  refetch: () => void;
  me: User | undefined;
};

export const JobDetails = ({ job, onBack, me, refetch }: JobDetailsProps) => {
  const intl = useIntl();
  const qc = useQueryClient();
  const { activeJobId } = useJobsContext();

  const relativeTime = useRelativeTime(
    job["own-status"]["state-ts"] ?? undefined,
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const api = useApi();

  const mutation = useMutation({
    mutationFn: (state: Job["own-status"]["state"]) =>
      Promise.allSettled([
        api(`/raptor/unit-statuses/${job["own-status"].id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/vnd.api+json" },
          body: JSON.stringify({
            data: {
              id: job["own-status"].id,
              type: "unit-statuses",
              attributes: { state },
            },
          }),
        }).then(() =>
          qc.invalidateQueries({
            predicate: (query) => {
              if (query.queryKey[0] === "raptor/jobs") return true;
              return false;
            },
          }),
        ),
        new Promise((r) => setTimeout(r, 2000)),
      ]).then(([result]) => {
        if (result.status === "rejected") {
          throw result.reason;
        }
        return result.value;
      }),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          defaultMessage: "Task completed successfully!",
        }),
        severity: "success",
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          defaultMessage: "An error occurred, please try again!",
        }),
        severity: "error",
      });
    },
  });

  const navigationURL = useMemo(() => {
    const customer = job?.details.intervention.customer;
    if (!customer) return null;

    let destination = "";
    if (customer["base-coords"]?.lat && customer["base-coords"]?.lng) {
      destination = `${customer["base-coords"].lat},${customer["base-coords"].lng}`;
      return `https://waze.com/ul?ll=${destination}&navigate=yes`;
    } else if (customer.address) {
      const address = encodeURIComponent(customer.address);
      return `https://waze.com/ul?q=${address}&navigate=yes`;
    } else {
      return null;
    }
  }, [job]);

  return (
    <>
      <AppHeader onBack={onBack} me={me} />
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#F8F9FA",
        }}
      >
        <Box
          sx={{
            overflowY: "auto",
            pt: "55px",
            pb: 2,
            "-webkit-overflow-scrolling": "touch",
          }}
        >
          <Container sx={{ px: 2, py: 2 }}>
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 4,
                border: "1px solid #E0E0E0",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="raptor-sub-h"
                  sx={{
                    mb: 3,
                  }}
                >
                  {
                    job?.details.intervention["start-event"][
                      "localized-description"
                    ]
                  }
                </Typography>

                <Stack gap={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <NotificationImportantIcon
                        sx={{ color: "#ED6C02", fontSize: "20px" }}
                      />
                    </Box>
                    <Typography variant="raptor-main-p" sx={{ flex: 1 }}>
                      {job?.details.intervention["start-event"].category ?? ""}
                    </Typography>
                    <Link to={`/jobs/${job.id}/intervention-details`} asChild>
                      <IconButton sx={{ p: 0 }}>
                        <ArrowForwardIosIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Link>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Person sx={{ color: "#1976D2", fontSize: "20px" }} />
                    </Box>
                    <Typography variant="raptor-main-p" sx={{ flex: 1 }}>
                      {job?.details.intervention.customer.name ?? ""}
                    </Typography>
                    <Link
                      to={`/jobs/${job.id}/intervention-details?scroll=customer`}
                      asChild
                    >
                      <IconButton sx={{ p: 0 }}>
                        <ArrowForwardIosIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Link>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LocationOn sx={{ color: "#2e7d32", fontSize: "20px" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="raptor-main-p"
                        sx={{
                          wordBreak: "break-all",
                        }}
                      >
                        {job?.details.intervention.customer.address ?? ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AccessTime sx={{ color: "#f57c00", fontSize: "20px" }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="raptor-main-p">
                        {formatDateTime(intl, job["created-ts"])}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: (theme) => theme.palette.primary.main,
                        gap: 2,
                      }}
                    >
                      {job["own-status"].state === "accepted" ? (
                        <PriorityHighIcon
                          color="error"
                          sx={{ fontSize: "20px" }}
                        />
                      ) : job["own-status"].state === "pending" ? (
                        <Flag sx={{ fontSize: "20px" }} />
                      ) : (
                        <CheckCircleOutline sx={{ fontSize: "20px" }} />
                      )}
                      <Typography variant="raptor-sub-p">
                        {intl.formatMessage(
                          statusConfig[job["own-status"].state],
                        )}
                      </Typography>
                    </Box>

                    {job["own-status"].state !== "pending" && (
                      <Typography
                        sx={{ textAlign: "center" }}
                        variant="caption"
                      >
                        {relativeTime}
                      </Typography>
                    )}
                  </Box>

                  {(() => {
                    const actions = [
                      {
                        label: intl.formatMessage({
                          defaultMessage: "Confirm",
                        }),
                        onClick: () => mutation.mutate("accepted"),
                        state: "pending",
                        icon: <CheckCircleOutline />,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: "Go",
                        }),
                        onClick: () => mutation.mutate("started"),
                        state: "accepted",
                        icon: <PlayArrow />,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: "Arrived",
                          id: "JobDetailsArrivedStatus",
                        }),
                        onClick: () => mutation.mutate("arrived"),
                        state: "started",
                        icon: <LocationOn />,
                      },
                      {
                        label: intl.formatMessage({
                          defaultMessage: "Finished",
                          description: "JobDetails finished status",
                        }),
                        onClick: () => mutation.mutate("finished"),
                        state: "arrived",
                        icon: <Flag />,
                      },
                    ];

                    const next = actions.find(
                      (a) => a.state === job["own-status"].state,
                    );
                    if (!next) return null;

                    const isDisabled =
                      mutation.isPending ||
                      (next.state === "accepted" &&
                        activeJobId !== null &&
                        activeJobId !== job.id);

                    return (
                      <Box sx={{ mt: 1 }}>
                        {next.state === "accepted" &&
                          activeJobId &&
                          !mutation.isPending && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {intl.formatMessage({
                                defaultMessage:
                                  "You already have an accepted task, cannot accept another.",
                              })}
                            </Typography>
                          )}
                        <Button
                          onClick={next.onClick}
                          variant="contained"
                          color="primary"
                          fullWidth
                          size="large"
                          startIcon={
                            mutation.isPending ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              next.icon
                            )
                          }
                          disabled={isDisabled}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                        >
                          <Typography variant="raptor-button-text">
                            {mutation.isPending
                              ? intl.formatMessage({
                                  defaultMessage: "Update...",
                                })
                              : next.label}
                          </Typography>
                        </Button>
                      </Box>
                    );
                  })()}

                  <Button
                    variant="outlined"
                    startIcon={<Navigation />}
                    href={navigationURL ?? ""}
                    disabled={!navigationURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography variant="raptor-button-text" color="primary">
                      {intl.formatMessage({ defaultMessage: "Route planning" })}
                    </Typography>
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {(job.details["dispatch-config"].remarks ||
              Object.keys(job.details["dispatch-config"].custom ?? {}).length >
                0) && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 4,
                  border: "1px solid #E0E0E0",
                  overflow: "hidden",
                  p: 2,
                }}
              >
                <Typography variant="raptor-main-p">
                  {intl.formatMessage({ defaultMessage: "Instructions:" })}
                </Typography>

                {job.details["dispatch-config"].remarks && (
                  <Box
                    sx={{
                      maxHeight: 250,
                      overflowY: "auto",
                      fontSize: "14.4px",
                      "-webkit-overflow-scrolling": "touch",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        job.details["dispatch-config"].remarks,
                      ),
                    }}
                  />
                )}

                {job.details["dispatch-config"]["custom-fields"]?.map(
                  ({ name }, idx, arr) => (
                    <Box key={idx} sx={{ mb: idx < arr.length - 1 ? 2 : 0 }}>
                      <Typography variant="raptor-main-p" sx={{ mb: 1 }}>
                        {name}:
                      </Typography>
                      <Typography variant="raptor-sub-p">
                        {job.details["dispatch-config"].custom?.[name] || "-"}
                      </Typography>
                    </Box>
                  ),
                )}
              </Card>
            )}

            <Box
              sx={{
                display:
                  job["own-status"].state === "arrived" &&
                  job["own-status"].primary
                    ? "block"
                    : "none",
              }}
            >
              <ResponseJobReportForm
                formValues={job?.values ?? {}}
                template={job.details["form-template"].sections}
                jobId={job.id}
              />
            </Box>
            <PhotoGalleryCapture
              jobId={job.id}
              refetch={refetch}
              attachments={job.attachments}
            />
            <Box
              sx={{
                display:
                  job["own-status"].state === "arrived" ? "block" : "none",
              }}
            >
              <SignatureField
                jobId={job.id}
                attachments={job.attachments}
                refetch={refetch}
              />
            </Box>
            <JobLogChat />

            <Box
              sx={{
                display:
                  job["own-status"].state === "arrived" ? "block" : "none",
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate("finished")}
                startIcon={
                  mutation.isPending ? <CircularProgress size={20} /> : <Flag />
                }
                fullWidth
              >
                <Typography variant="raptor-button-text">
                  {intl.formatMessage({ defaultMessage: "Finished" })}
                </Typography>
              </Button>
            </Box>

            <Box sx={{ mt: 10 }}>
              <CallbackRequest jobId={job.id} />
            </Box>
          </Container>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          slots={{ transition: Slide }}
        >
          <Alert
            severity={snackbar.severity as "success" | "error"}
            sx={{
              borderRadius: 3,
              "& .MuiAlert-icon": {
                fontSize: "1.2rem",
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};
